import { CanopyClient } from '@canopy-pte-ltd/canopy-toolkit';
import { AxiosResponse } from 'axios';
import { OptionsType, OptionTypeBase } from 'react-select/src/types';
class MMClient extends CanopyClient {
    [x: string]: any;
    constructor(apiServer: string) {
        super({
            apiServer,
            persistSession: true,
            apiVersion: 'v2',
            appId: 'canopy_manager',
        });
        this.abortControllerMap = new Map();
    }

    getWithAbortController(url: any, options: any) {
        if (this.abortControllerMap?.has(url)) {
            this.abortControllerMap.get(url)?.abort();
            this.abortControllerMap.delete(url);
        }
        this.abortControllerMap.set(url, new AbortController());
        return this.get(url, {
            ...options,
            signal: this.abortControllerMap.get(url)?.signal,
        });
    }
    async fetcher(url: string, endpointVersion: string) {
        if (!url) {
            return null;
        }
        try {
            const response: any =
                (await this.get(url, { apiVersion: endpointVersion })) || {};
            if (response.status !== 200) {
                throw 'An error occurred while fetching the data.';
            }
            return response?.data;
        } catch (e: any) {
            throw e?.response?.data?.error;
        }
    }

    async getMenu({ deviceType }: Record<string, string | undefined>) {
        if (!deviceType) {
            return;
        }
        try {
            const { data }: AxiosResponse = await this.get('menu', {
                queryParams: { device: deviceType },
            });
            const menus: any = fetchMenus(data);
            return { menus, deviceType };
        } catch (e: any) {
            throw e?.response?.data?.error;
        }
    }

    async getAllMenu() {
        try {
            const { data }: AxiosResponse = await this.get('menu/all', {});
            return data?.menus;
        } catch (e: any) {
            throw e?.response?.data?.error;
        }
    }

    async addMenuMapping(payload: any) {
        try {
            const { data } = await this.post(`/menu-mapping`, {
                data: payload,
                apiVersion: 'v2',
            });
            return data;
        } catch (e: any) {
            throw e?.response?.data?.errors
                ? JSON.stringify(e?.response?.data?.errors)
                : e?.response?.data?.error;
        }
    }

    async getUsers(
        inputValue: string,
        roleIds: string | number,
        callback: (options: OptionsType<OptionTypeBase>) => void
    ) {
        try {
            const { data } = await this.get(`users/dropdown`, {
                queryParams: { keyword: inputValue, role_ids: String(roleIds) },
            });
            Array.isArray(data) ? callback(data) : callback([]);
        } catch (e: any) {
            throw e?.response?.data?.error;
        }
    }

    async getUsersByName(
        inputValue: string,
        roleIds: string | number,
        callback: (options: OptionsType<OptionTypeBase>) => void
    ) {
        try {
            const { data } = await this.get(`uploads/usernames`, {
                queryParams: { keyword: inputValue, role_ids: String(roleIds) },
            });
            Array.isArray(data) ? callback(data) : callback([]);
        } catch (e: any) {
            throw e?.response?.data?.error;
        }
    }

    async getClients(
        inputValue: string,
        callback?: (options: OptionsType<OptionTypeBase>) => void
    ) {
        try {
            const { data } = await this.get(`users/dropdown`, {
                queryParams: { keyword: inputValue, role_ids: '4' },
            });
            if (callback) {
                Array.isArray(data) ? callback(data) : callback([]);
                return;
            }
            return Array.isArray(data) ? data : [];
        } catch (e: any) {
            throw e?.response?.data?.error;
        }
    }

    async checkUserDb(userId: string | number) {
        try {
            await this.get('strategies/types', {
                queryParams: { user_ids: String(userId) },
            });
            return true;
        } catch (e) {
            return false;
        }
    }

    async getChildAccounts(userId: string) {
        const queryParams: any =
            userId !== 'all' ? { parent_user_ids: userId } : {};
        try {
            const { data: response }: any = await this.get('child-accounts', {
                queryParams,
            });
            if (!response?.error) {
                return response;
            }
            return {
                error:
                    response?.error ||
                    'An error occurred while fetching the data.',
            };
        } catch (e: any) {
            return {
                error:
                    e?.response?.data?.error ||
                    'An error occurred while fetching the data.',
            };
        }
    }
    async getFiltersChildAccounts(userId: string) {
        const queryParams: any =
            userId !== 'all' ? { parent_user_ids: userId } : {};
        try {
            const { data: response }: any = await this.get(
                'filters/child-accounts',
                {
                    queryParams,
                }
            );
            if (!response?.error) {
                return response;
            }
            return {
                error:
                    response?.error ||
                    'An error occurred while fetching the data.',
            };
        } catch (e: any) {
            return {
                error:
                    e?.response?.data?.error ||
                    'An error occurred while fetching the data.',
            };
        }
    }

    async getThemeConfig() {
        const { data } = await this.get(`partner/preference`, {
            headers: {
                ...this.headers,
                'x-app-locale': 'en', // TODO: this needs to change once @gunjan's PR is merged.
            },
        });
        return data;
    }

    async getPartners() {
        try {
            const response: any = await this.get(`partner/all`);
            if (!response?.error) {
                return response?.data?.partners;
            }
            return { error: response.error };
        } catch (e: any) {
            return { error: e?.response?.data?.error };
        }
    }

    async getApps() {
        try {
            const response: any = await this.get(`menu/apps`);
            if (!response?.error) {
                return response?.data?.apps;
            }
            return { error: response.error };
        } catch (e: any) {
            return { error: e?.response?.data?.error };
        }
    }
}

export default new MMClient(
    process.env.REACT_APP_FLASH_DB_API || 'https://dev-api.kurtosys.org/'
);

export const ROLES = [
    {
        label: 'ui.partneradmin',
        value: 5,
    },
    { label: 'ui.rm', value: 8 },
    { label: 'ui.customer', value: 4 },
    { label: 'ui.sa', value: 1 },
];

export const ROLES_ID: Record<number, string> = {
    4: 'Customer',
    5: 'Partneradmin',
    8: 'RelationshipManager',
    1: 'Superadmin',
};

export const fetchMenus = (data: any) => {
    const { details }: any = data;
    const { menus } = details.find(
        (detail: any) => detail.app_id === 'manager'
    ) || { menus: [] };
    return menus;
};
